import {forkJoin, Subscription} from 'rxjs';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {
  ICompanyDetails,
  IKompraCompany,
  IOrganizationInfo,
  IOrganizationInfoSearch,
  IOrganizationType,
  IOrganizationTypeShortName,
  OrganizationService,
  TypeIds,
  TypeIdsTitle
} from '../select-item/organizations.service';
import {UserInfo} from '../../../components/header/header.component';
import {ToastrService} from 'ngx-toastr';
import {HttpErrorResponse} from '@angular/common/http';
import {debounceTime, distinctUntilChanged, skip} from 'rxjs/operators';

@Component({
  selector: 'app-add-organization',
  templateUrl: './add-organization.component.html',
  styleUrls: ['./add-organization.component.scss']
})
export class AddOrganizationComponent implements OnInit, OnDestroy {

  @Input() organization: IOrganizationInfoSearch = null;
  @Input() key: IOrganizationTypeShortName = null;
  @Output() closed = new EventEmitter<string>();

  developerTitle: string = '';

  organizationInfo: IOrganizationInfo;
  createOrgForm: UntypedFormGroup;

  additionalPhones: Array<ICompanyDetails> = [];
  additionalEmails: Array<ICompanyDetails> = [];
  additionalWebsites: Array<ICompanyDetails> = [];

  typeList: Array<IOrganizationType> = [];
  user: UserInfo = null;
  isBDAminUser: boolean = false;

  loading: boolean = true;
  loadingFromKompra: boolean = false;

  oldOrganizationBin: string = null;
  emailPattern = "^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$";
  emailPatternReg = new RegExp(this.emailPattern, 'i');

  prevReceivedOrganizationBin: string = null;

  isUpdatedFromKompra: boolean = false;
  binSubscription: Subscription;
  saveLoading: boolean = false;

  constructor(private organizationService: OrganizationService, public toastr: ToastrService) { }

  get isCreateMode() {
    return !this.organization || (this.organization && !this.organization.bin);
  }

  ngOnInit() {
    this.setDeveloperTitleByKey();
    this.setIsBDAminUser();
    this.initForm();
    this.subscribeToBinControl();
    this.getData();
  }

  setDeveloperTitleByKey() {
    if (TypeIds[this.key]) {
      if (TypeIdsTitle[this.key]) {
        this.developerTitle = TypeIdsTitle[this.key];
      }
    }
  }

  getData() {
    if (this.organization) {
      forkJoin([
        this.organizationService.getOrganizationTypes(),
        this.organizationService.getOrganizationByBin(this.organization.bin)
      ]).subscribe(([typeList, organizationInfo]) => {
        this.typeList = typeList;
        this.organizationInfo = organizationInfo;
        this.oldOrganizationBin = this.organizationInfo.bin;

        if (this.organizationInfo.isValidBIN) {
          this.createOrgForm.controls['bin'] = new UntypedFormControl({ value: this.organizationInfo.bin, disabled: true }, [Validators.required]);
        } else if (!this.organizationInfo.isValidBIN) {
          this.subscribeToBinControl();
        }
        this.initOrganizationForm(this.organizationInfo);
      });
    } else {
      this.organizationService.getOrganizationTypes().subscribe(res => {
        this.typeList = res;
        if (TypeIds[this.key]) {
          let findedTypeIndex = this.typeList.findIndex(x => x.id === TypeIds[this.key]);
          if (findedTypeIndex !== -1) {
            this.typeList[findedTypeIndex].active = true;
          }
        }
        this.loading = false;
      });

      if (this.additionalPhones.length === 0) {
        this.addAdditionalPhone(0);
      }
      if (this.additionalEmails.length === 0) {
        this.addAdditionalEmail(0);
      }
      if (this.additionalWebsites.length === 0) {
        this.addAdditionalWebsite(0);
      }
    }
  }

  setIsBDAminUser() {
    let user = localStorage.getItem('user');
    if (user) {
      this.user = JSON.parse(user);
      if (this.user.roles.some(e => e === 'BDAdmin')) {
        this.isBDAminUser = true;
      }
    }
  }

  initForm() {
    this.createOrgForm = new UntypedFormGroup({
      bin: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
      title: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
      director: new UntypedFormControl({ value: '', disabled: true }, []),

      physicalAddress: new UntypedFormControl({ value: '', disabled: true }, []),
      registerAddress: new UntypedFormControl({ value: '', disabled: true }, []),
      contactPerson: new UntypedFormControl(''),

      mainPhone: new UntypedFormControl({ value: '', disabled: true }, []),
      mainWebsite: new UntypedFormControl({ value: '', disabled: true }, []),
      mainEmail: new UntypedFormControl({ value: '', disabled: true }, []),
    });
  }

  subscribeToBinControl() {
    this.binSubscription = this.createOrgForm.controls['bin'].valueChanges.pipe(
      skip(this.isCreateMode ? 0 : 1),
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe((value: string) => {
      if (value) {
        this.isUpdatedFromKompra = false;
        var reg = /^\d+$/;
        if (!reg.test(value)) {
          value = value.toString().replace(/\D+/g, "");
        }
        if (value.length > 12) {
          value = value.slice(0, 12);
        }

        if (value.length < 12) {
          this.clearOrganizationForm();
        }

        if (value.length === 12 && this.prevReceivedOrganizationBin !== value) {
          this.prevReceivedOrganizationBin = this.createOrgForm.controls['bin'].value;
          this.getOrganizationByBin(this.createOrgForm.controls['bin'].value);
        }

        this.createOrgForm.controls['bin'].patchValue(value, { emitEvent: false });
      }
    });


  }


  initOrganizationForm(organizationInfo: IOrganizationInfo, needToUpdateFromKompra: boolean = false) {
    let formObject = {
      bin: organizationInfo.bin,
      title: organizationInfo.title,
      director: organizationInfo.director,
      physicalAddress: organizationInfo.physicalAddress,
      registerAddress: organizationInfo.registerAddress,
      contactPerson: organizationInfo.contactPerson,
      mainPhone: '',
      mainEmail: '',
      mainWebsite: '',
    }
    let mainPhone: ICompanyDetails = organizationInfo.details.find(x => x.kind === 'phone' && !x.extra);
    if (mainPhone) {
      formObject['mainPhone'] = mainPhone.value;
    }

    let mainEmail: ICompanyDetails = organizationInfo.details.find(x => x.kind === 'email' && !x.extra);
    if (mainEmail) {
      formObject['mainEmail'] = mainEmail.value;
    }
    let mainWebsite: ICompanyDetails = organizationInfo.details.find(x => x.kind === 'web' && !x.extra);
    if (mainWebsite) {
      formObject['mainWebsite'] = mainWebsite.value;
    }

    this.createOrgForm.patchValue(formObject);



    this.additionalPhones = organizationInfo.details.filter(x => x.kind === 'phone' && x.extra);
    if (this.additionalPhones.length === 0) {
      this.addAdditionalPhone(0);
    }
    this.additionalEmails = organizationInfo.details.filter(x => x.kind === 'email' && x.extra);
    if (this.additionalEmails.length === 0) {
      this.addAdditionalEmail(0);
    }
    this.additionalWebsites = organizationInfo.details.filter(x => x.kind === 'web' && x.extra);
    if (this.additionalWebsites.length === 0) {
      this.addAdditionalWebsite(0);
    }


    organizationInfo.typeList.map(type => {
      let findedTypeIndex = this.typeList.findIndex(x => x.id === type);
      if (findedTypeIndex !== -1) {
        this.typeList[findedTypeIndex].active = true;

        if (!this.isBDAminUser) {
          this.typeList[findedTypeIndex].disabled = true;
        }
      }
    });

    if (needToUpdateFromKompra) {
      this.searchAndUpdateOrganizationFromKompra();
    }
    this.loading = false;
  }


  clearOrganizationForm() {
    let formObject = {
      bin: this.createOrgForm.controls['bin'].value,
      title: '',
      director: '',
      physicalAddress: '',
      registerAddress: '',
      contactPerson: '',
      mainPhone: '',
      mainEmail: '',
      mainWebsite: '',
    }
    this.createOrgForm.patchValue(formObject, { emitEvent: false});


    this.additionalPhones = [];
    this.addAdditionalPhone(0);
    this.additionalEmails = [];
    this.addAdditionalEmail(0);
    this.additionalWebsites = [];
    this.addAdditionalWebsite(0);

    this.loading = false;
    this.loadingFromKompra = false;
    this.isUpdatedFromKompra = false;
  }

  getOrganizationByBin(bin: string) {
    this.loading = true;
    this.organizationService.getOrganizationByBin(bin).subscribe(organizationInfo => {
      this.organizationInfo = organizationInfo;

      this.organization = {
        bin: bin,
        title: this.organizationInfo.title,
        types: this.organizationInfo.typeList,
      }
      this.oldOrganizationBin = this.organizationInfo.bin;
      this.initOrganizationForm(this.organizationInfo, true);
    }, (error: HttpErrorResponse) => {
      this.loading = false;
      this.clearOrganizationForm();

      if (error && error.error && error.error.message) {
        this.toastr.error(error.error.message, 'Ошибка');
      }
    });
  }

  searchAndUpdateOrganizationFromKompra() {
    this.loadingFromKompra = true;
    let bin = this.createOrgForm.get('bin').value;

    this.organizationService.getCompanyFromKompra(bin).subscribe(res => {
      let company: IKompraCompany = res;
      if (company.bin) {
        this.createOrgForm.get('title').patchValue(company.name);
        this.createOrgForm.get('director').patchValue(company.owner);
        this.createOrgForm.get('physicalAddress').patchValue(company.factAddress);
        this.createOrgForm.get('registerAddress').patchValue(company.lawAddress);

        this.createOrgForm.get('mainPhone').patchValue(company.phone);
        this.createOrgForm.get('mainEmail').patchValue(company.email);
        this.createOrgForm.get('mainWebsite').patchValue(company.website);

        this.isUpdatedFromKompra = true;
      } else {
        this.isUpdatedFromKompra = false;
        this.toastr.warning('Данные о компании не найдены в Kompra.kz!', '');
      }
      this.loadingFromKompra = false;
    },
      (error: HttpErrorResponse) => {
        this.isUpdatedFromKompra = false;
        this.loadingFromKompra = false;
        this.toastr.clear();
        if (error.status === 403) {
          this.toastr.warning('Превышено число обращений к сервису kompra.kz за сутки!', '');
        } else {
          this.toastr.warning('Данные о компании не найдены в Kompra.kz!', '');
        }
    });
  }

  createItem() {
    if (!this.createOrgForm.get('contactPerson').value ||
      !this.createOrgForm.get('bin').value ||
      !this.createOrgForm.get('title').value ||
      this.typeList.every(x => !x.active) ||
      this.additionalEmails.some(x => x.errorEmail) ||
      this.additionalEmails.some(x => !x.value) ||
      this.additionalPhones.some(x => !x.value || (x.value && x.value.length < 11))
    ) {
      this.toastr.clear();
      if (this.typeList.every(x => !x.active)) {
        this.toastr.warning('Должен быть выбран хотя бы один тип организации!', '');
      }
      if (!this.createOrgForm.get('bin').value) {
        this.toastr.warning('БИН компании должен быть заполнен!', '');
      }
      if (!this.createOrgForm.get('contactPerson').value) {
        this.toastr.warning('ФИО Контактного лица должно быть заполнено!');
      }
      if (!this.createOrgForm.get('title').value) {
        this.toastr.warning('Наименование организации должно быть заполнено!');
      }
      if (this.additionalPhones.some(x => !x.value || (x.value && x.value.length < 11))) {
        this.toastr.warning('Дополнительный телефон должен быть заполнен!');
      }
      if (this.additionalEmails.some(x => x.value === '')) {
        this.toastr.warning('Дополнительный email должен быть заполнен!', '');
      }
      if (this.additionalEmails.some(x => x.errorEmail)) {
        this.toastr.warning('Ошибка в формате дополнительного email!', '');
      }
      return;
    }

    this.saveLoading = true;

    let res: IOrganizationInfo = {
      bin: this.createOrgForm.get('bin').value || '',
      title: this.createOrgForm.get('title').value || '',
      director: this.createOrgForm.get('director').value || '',
      physicalAddress: this.createOrgForm.get('physicalAddress').value || '',
      registerAddress: this.createOrgForm.get('registerAddress').value || '',
      contactPerson: this.createOrgForm.get('contactPerson').value || '',
      typeList: this.typeList.filter(x => x.active).map(elem => elem.id),
      details: [],
    }

    if (this.oldOrganizationBin && res.bin !== this.oldOrganizationBin) {
      res.oldBin = this.oldOrganizationBin;
    }

    let mainPhoneValue = this.createOrgForm.get('mainPhone').value;
    if (!mainPhoneValue) {
      mainPhoneValue = '';
    }
    let mainPhone: ICompanyDetails = { extra: false, kind: 'phone', order: 1, value: mainPhoneValue, source: 'kompra.kz' };
    res.details.push(mainPhone);

    let mainEmailValue = this.createOrgForm.get('mainEmail').value;
    if (!mainEmailValue) {
      mainEmailValue = '';
    }
    let mainEmail: ICompanyDetails = { extra: false, kind: 'email', order: 1, value: mainEmailValue, source: 'kompra.kz' };
    res.details.push(mainEmail);


    let mainWebsiteValue = this.createOrgForm.get('mainWebsite').value;
    if (!mainWebsiteValue) {
      mainWebsiteValue = '';
    }
    let mainWebsite: ICompanyDetails = { extra: false, kind: 'web', order: 1, value: mainWebsiteValue, source: 'kompra.kz' };
    res.details.push(mainWebsite);


    this.additionalPhones = this.additionalPhones.map((elem, index) => {
      elem.order = index + 1;
      return elem;
    });
    this.additionalPhones.filter(x => x.value).map(elem => {
      res.details.push(elem);
    });

    this.additionalEmails = this.additionalEmails.map((elem, index) => {
      elem.order = index + 1;
      return elem;
    });
    this.additionalEmails.filter(x => x.value).map(elem => {
      res.details.push(elem);
    });

    this.additionalWebsites = this.additionalWebsites.map((elem, index) => {
      elem.order = index + 1;
      return elem;
    });
    this.additionalWebsites.filter(x => x.value).map(elem => {
      res.details.push(elem);
    });
    this.organizationInfo = res;

    if (this.organization && this.organization.bin) {
      this.organizationService.updateOrganization(this.organizationInfo).subscribe(res => {
        this.toastr.success('Организация успешно обновлена!', 'Успешно');
        this.closed.emit(this.organizationInfo.bin);
        this.saveLoading = false;
      }, (error: HttpErrorResponse) => {
        this.saveLoading = false;
        if (error && error.error && error.error.message) {
          this.toastr.error(error.error.message, 'Ошибка');
        }
      });
    } else {
      this.organizationService.createOrganization(this.organizationInfo).subscribe(res => {
        this.toastr.success('Организация успешно создана!', 'Успешно');
        this.closed.emit(this.organizationInfo.bin);
        this.saveLoading = false;
      }, (error: HttpErrorResponse) => {
          this.saveLoading = false;
        if (error && error.error && error.error.message) {
          this.toastr.error(error.error.message, 'Ошибка');
        }
      });
    }
  }


  //#region Phone, Websites, Emails operations
  removeAdditionPhone(emailAddressItem: string | any, phoneIndex: number) {
    if (confirm('Вы действительно хотите удалить номер телефона: ' + this.additionalPhones[phoneIndex].value)) {
      this.additionalPhones.splice(phoneIndex, 1);
    }
  }

  addAdditionalPhone(emailIndex: number) {
    let order;
    if (this.additionalPhones.length > 0) {
      order = Math.max.apply(null, this.additionalPhones.map(x => x.order)) + 1;
    } else {
      order = 1;
    }

    this.additionalPhones.splice(emailIndex + 1, 0, {
      extra: true,
      kind: 'phone',
      order: order,
      value: '',
      source: 'user',
    });
  }


  removeAdditionlEmail(emailAddressItem: string | any, emailIndex: number) {
    if (confirm('Вы действительно хотите удалить email адрес: ' + this.additionalEmails[emailIndex].value)) {
      this.additionalEmails.splice(emailIndex, 1);
    }
  }

  addAdditionalEmail(emailIndex: number) {
    let order;
    if (this.additionalEmails.length > 0) {
      order = Math.max.apply(null, this.additionalEmails.map(x => x.order)) + 1;
    } else {
      order = 1;
    }

    this.additionalEmails.splice(emailIndex + 1, 0, {
      extra: true,
      kind: 'email',
      order: order,
      value: '',
      source: 'user',
    });
  }


  removeAdditionWebsite(emailAddressItem: string | any, websiteIndex: number) {
    if (confirm('Вы действительно хотите удалить веб-сайт: ' + this.additionalWebsites[websiteIndex].value)) {
      this.additionalWebsites.splice(websiteIndex, 1);
    }
  }

  addAdditionalWebsite(emailIndex: number) {
    let order;
    if (this.additionalWebsites.length > 0) {
      order = Math.max.apply(null, this.additionalWebsites.map(x => x.order)) + 1;
    } else {
      order = 1;
    }

    this.additionalWebsites.splice(emailIndex + 1, 0, {
      extra: true,
      kind: 'web',
      order: order,
      value: '',
      source: 'user',
    });
  }

  //#endregion Phone, Websites, Emails operations



  emailValidate(item: ICompanyDetails) {
    if (!item.value || this.emailPatternReg.test(item.value)) {
      item.errorEmail = false;
    } else {
      item.errorEmail = true;
    }
  }

  close() {
    this.closed.emit('');
  }


  ngOnDestroy() {
    if (this.binSubscription && !this.binSubscription.closed) {
      this.binSubscription.unsubscribe();
    }
  }

}
